import type { Board } from '../../lib/api';

/** One hit of `GET /api/search` (crates/server/src/api/search.rs). */
export interface SearchResult {
  board: Board;
  titleMatch: boolean;
  /** Title with matches wrapped in U+E000 … U+E001, when the title matched by words. */
  title: string | null;
  snippet: string | null;
  source: 'items' | 'comments' | 'content' | null;
  /** Matching items (up to five), for `?focus=`. */
  items: string[];
  /** Matching comment threads, for `?comment=`. */
  threads: string[];
}

export const MARK_START = '';
export const MARK_END = '';

/** Opens the board at the first matching item, else the first matching comment thread. */
export function searchLink(r: SearchResult): string {
  if (r.source === 'items' && r.items[0]) return `/b/${r.board.id}?focus=${encodeURIComponent(r.items[0])}`;
  if (r.source === 'comments' && r.threads[0]) return `/b/${r.board.id}?comment=${encodeURIComponent(r.threads[0])}`;
  return `/b/${r.board.id}`;
}

/** Splits marked text into plain and highlighted runs. */
export function highlightRuns(text: string): { text: string; hit: boolean }[] {
  const out: { text: string; hit: boolean }[] = [];
  let hit = false;
  let buf = '';
  for (const ch of text) {
    if (ch === MARK_START || ch === MARK_END) {
      if (buf) out.push({ text: buf, hit });
      buf = '';
      hit = ch === MARK_START;
    } else {
      buf += ch;
    }
  }
  if (buf) out.push({ text: buf, hit });
  return out;
}

/** Marks every case-insensitive occurrence of `q` (titles found by substring, not by words). */
export function markSubstring(text: string, q: string): string {
  const needle = q.trim().toLocaleLowerCase();
  if (!needle) return text;
  const lower = text.toLocaleLowerCase();
  if (lower.length !== text.length) return text;
  let out = '';
  let from = 0;
  for (let i = lower.indexOf(needle); i >= 0; i = lower.indexOf(needle, i + needle.length)) {
    out += text.slice(from, i) + MARK_START + text.slice(i, i + needle.length) + MARK_END;
    from = i + needle.length;
  }
  return out + text.slice(from);
}
